import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import { type ApiResult, classifyResponse, isRetryable, resultMessage } from './api-result';
import { type IRelaySession, signRelay } from './media-relay';

/**
 * Standalone client for api.core's polling-unit cancellation ledger
 * (`/api/v1/Election/{electionId}/cancellations`, STORY-71 v2).
 *
 * Same discipline as `agent-presence-api.ts`: no 401→logout interceptor (a failed report must
 * never sign the agent out mid-incident), a 20 s timeout (the app default is none), and every
 * status resolves so the caller classifies the body instead of catching.
 */
const cancellationApi = axios.create({ baseURL: COREAPI, timeout: 20_000 });

cancellationApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// api.core sometimes answers JSON with a Content-Type axios will not auto-parse.
cancellationApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

/**
 * Build-time switch: on only where the build's env sets `REACT_APP_PU_CANCELLATION_ENABLED=true`
 * — today `.env.production` (the staging image, which calls ielect-api.ngrok.app), NOT
 * `.env.cluster` (the `-prod` image). Turn it on for a host only once the api.core image carrying
 * `POST …/cancellations/report` is ROLLED there. Support can switch it per device without a
 * build: localStorage `ielect-pu-cancellation` = `on` | `off`.
 */
export const PU_CANCELLATION_ENABLED = process.env.REACT_APP_PU_CANCELLATION_ENABLED === 'true';
export const PU_CANCELLATION_OVERRIDE_KEY = 'ielect-pu-cancellation';

export const puCancellationEnabled = (): boolean => {
	try {
		const override = localStorage.getItem(PU_CANCELLATION_OVERRIDE_KEY);
		if (override === 'on') return true;
		if (override === 'off') return false;
	} catch {
		// Storage blocked — fall through to the build default.
	}
	return PU_CANCELLATION_ENABLED;
};

// ---------------------------------------------------------------------------
// Contract (enums travel BY NAME — `StringEnumConverter` on the server)
// ---------------------------------------------------------------------------

export type CancellationReason = 'OverVoting' | 'Violence' | 'BvasFailure' | 'MaterialsNotDelivered' | 'CourtOrder' | 'Other';
export type CancellationSource = 'Admin' | 'Agent';
export type CancellationReviewStatus = 'Unreviewed' | 'Confirmed';
export type CancellationState = 'Active' | 'Withdrawn' | 'Reinstated';
export type EvidenceKind = 'Image' | 'Video' | 'Audio';

export interface ICancellationRow {
	id: number;
	electionId: number;
	partyId: number;
	pollingUnitId?: number;
	pollingUnitCode: string;
	pollingUnitName?: string | null;
	wardName?: string | null;
	wardCode?: string | null;
	lgaName?: string | null;
	lgaCode?: string | null;
	reason: CancellationReason;
	notes?: string | null;
	cancelledAt: string;
	recordedAt: string;
	recordedBy?: string | null;
	recordedByUserId?: number | null;
	/** The v2 fields below are absent on a v1 (pre-capabilities) server. */
	source?: CancellationSource;
	reviewStatus?: CancellationReviewStatus;
	reviewedBy?: string | null;
	reviewedAt?: string | null;
	state?: CancellationState;
	stateChangedAt?: string | null;
	stateChangedBy?: string | null;
	stateReason?: string | null;
	/** Null unless source=Agent, state=Active, reviewStatus=Unreviewed. Server-computed. */
	withdrawableUntil?: string | null;
	latitude?: number | null;
	longitude?: number | null;
	accuracyMeters?: number | null;
	clientReferenceId?: string | null;
	vesrIncidentId?: string | null;
	evidenceCount?: number;
}

export interface ICancellationCapabilities {
	agentReport: boolean;
	withdrawWindowMinutes: number;
	review: boolean;
	evidence: boolean;
	impact: boolean;
	liveEvents: boolean;
	candidateSms: boolean;
	resultsCancellationAware: boolean;
}

/** What a 404 on `GET capabilities` means: the v1 ledger — bulk POST / DELETE / GET only. */
export const BASELINE_CAPABILITIES: ICancellationCapabilities = {
	agentReport: false,
	withdrawWindowMinutes: 0,
	review: false,
	evidence: false,
	impact: false,
	liveEvents: false,
	candidateSms: false,
	resultsCancellationAware: false,
};

export type CapabilitiesOutcome =
	| { mode: 'v2'; caps: ICancellationCapabilities }
	| { mode: 'baseline'; caps: ICancellationCapabilities }
	/** Offline, 5xx, 401 — say nothing about the server; ask again later. */
	| { mode: 'unknown' };

export interface IReportRequest {
	pollingUnitCode: string;
	reason: CancellationReason;
	notes?: string;
	cancelledAt?: string;
	clientReferenceId: string;
	latitude?: number;
	longitude?: number;
	accuracyMeters?: number;
}

export interface IReportResponse {
	/** Null only on the v1 fallback when the unit was already cancelled (the bulk call returns codes, not rows). */
	row: ICancellationRow | null;
	replayed: boolean;
	alreadyCancelled: boolean;
}

/**
 * api.core's refusal codes for one evidence file. Only {@link TERMINAL_EVIDENCE_CODES} (and an
 * HTTP 413) mean the same bytes can never be accepted; the rest describe a server or ledger
 * state that changes — a report not visible yet, an asset MediaStash hasn't finished storing.
 */
export const EVIDENCE_CODES = {
	mediaRejected: 'MEDIA_REJECTED',
	belongsElsewhere: 'EVIDENCE_BELONGS_ELSEWHERE',
	cancellationNotFound: 'CANCELLATION_NOT_FOUND',
	notAllowed: 'EVIDENCE_NOT_ALLOWED',
	assetNotStored: 'ASSET_NOT_STORED',
	assetNotActive: 'ASSET_NOT_ACTIVE',
	assetTypeMismatch: 'ASSET_TYPE_MISMATCH',
	assetSizeMismatch: 'ASSET_SIZE_MISMATCH',
	assetFolderMismatch: 'ASSET_FOLDER_MISMATCH',
} as const;

export const TERMINAL_EVIDENCE_CODES: readonly string[] = [EVIDENCE_CODES.mediaRejected, EVIDENCE_CODES.belongsElsewhere];

export const RETRYABLE_EVIDENCE_CODES: readonly string[] = [
	EVIDENCE_CODES.cancellationNotFound,
	EVIDENCE_CODES.notAllowed,
	EVIDENCE_CODES.assetNotStored,
	EVIDENCE_CODES.assetNotActive,
	EVIDENCE_CODES.assetTypeMismatch,
	EVIDENCE_CODES.assetSizeMismatch,
	EVIDENCE_CODES.assetFolderMismatch,
];

/** Sending the same file again can never succeed. */
export const isTerminalEvidenceRefusal = (status: number, code?: string): boolean =>
	status === 413 || (!!code && TERMINAL_EVIDENCE_CODES.includes(code));

export interface IRegisterEvidenceRequest {
	kind: EvidenceKind;
	storagePublicId: string;
	/** Optional and ignored since the MediaStash relay — the server derives it. */
	url?: string;
	mime: string;
	bytes: number;
	durationSec?: number;
	capturedAt?: string;
	latitude?: number;
	longitude?: number;
}

// ---------------------------------------------------------------------------
// Result classification
// ---------------------------------------------------------------------------

// Re-exported: the feature clients and their tests import these from here.
export type { ApiResult };
export { classifyResponse, isRetryable, resultMessage };

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const isRow = (v: unknown): v is ICancellationRow =>
	isObject(v) && typeof v.id === 'number' && typeof v.pollingUnitCode === 'string';

const pickRowEnvelope = (data: unknown): ICancellationRow | undefined => {
	if (isObject(data) && isRow(data.row)) return data.row;
	return isRow(data) ? data : undefined;
};

export const toCapabilitiesOutcome = (status: number, body: unknown): CapabilitiesOutcome => {
	if (status === 404) return { mode: 'baseline', caps: BASELINE_CAPABILITIES };
	const res = classifyResponse(status, body, (data) => (isObject(data) ? data : undefined));
	if (res.kind !== 'ok') return { mode: 'unknown' };
	const d = res.data;
	return {
		mode: 'v2',
		caps: {
			agentReport: d.agentReport === true,
			withdrawWindowMinutes: Number.isFinite(Number(d.withdrawWindowMinutes)) ? Number(d.withdrawWindowMinutes) : 10,
			review: d.review === true,
			evidence: d.evidence === true,
			impact: d.impact === true,
			liveEvents: d.liveEvents === true,
			candidateSms: d.candidateSms === true,
			resultsCancellationAware: d.resultsCancellationAware === true,
		},
	};
};

export const toReportResult = (status: number, body: unknown): ApiResult<IReportResponse> =>
	classifyResponse(status, body, (data) => {
		if (!isObject(data) || !isRow(data.row)) return undefined;
		return { row: data.row, replayed: data.replayed === true, alreadyCancelled: data.alreadyCancelled === true };
	});

/** Maps the v1 bulk response for ONE code onto the single-report shape. */
export const toBaselineReportResult = (status: number, body: unknown, pollingUnitCode: string): ApiResult<IReportResponse> =>
	classifyResponse(status, body, (data) => {
		if (!isObject(data)) return undefined;
		const recorded = Array.isArray(data.recorded) ? data.recorded.filter(isRow) : [];
		const mine = recorded.find((r) => r.pollingUnitCode === pollingUnitCode) ?? recorded[0];
		if (mine) return { row: mine, replayed: false, alreadyCancelled: false };
		const already = Array.isArray(data.alreadyCancelled) && data.alreadyCancelled.includes(pollingUnitCode);
		return already ? { row: null, replayed: false, alreadyCancelled: true } : undefined;
	});

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

interface IRaw {
	status: number;
	body: unknown;
}

const send = async (run: () => Promise<{ status: number; data: unknown }>): Promise<IRaw> => {
	try {
		const res = await run();
		return { status: res.status, body: res.data };
	} catch {
		return { status: 0, body: undefined };
	}
};

const all = () => true;
const base = (electionId: number) => `/api/v1/Election/${electionId}/cancellations`;

/**
 * How long a "no evidence" answer (v2 with `evidence:false`, or the v1 baseline) is trusted.
 * Flags flip and api.core rolls mid-election; caching a no for the whole session parked every
 * file on the phone as `unsupported` until the app was restarted.
 */
export const NEGATIVE_CAPABILITIES_TTL_MS = 2 * 60_000;

const capabilitiesCache = new Map<number, { outcome: CapabilitiesOutcome; at: number }>();

const isFinalAnswer = (outcome: CapabilitiesOutcome) => outcome.mode === 'v2' && outcome.caps.evidence;

/**
 * Cached per election: a v2 answer with evidence for the session, anything less for
 * {@link NEGATIVE_CAPABILITIES_TTL_MS}; `unknown` is never cached. `recheckNegative` asks again
 * when the cached answer is a no — an agent's "Try again" must not be refused by a stale no.
 */
export const getCapabilities = async (
	electionId: number,
	{ fresh = false, recheckNegative = false, now = Date.now } = {}
): Promise<CapabilitiesOutcome> => {
	const cached = capabilitiesCache.get(electionId);
	const trusted =
		cached && (isFinalAnswer(cached.outcome) || (!recheckNegative && now() - cached.at < NEGATIVE_CAPABILITIES_TTL_MS));
	if (cached && !fresh && trusted) {
		return cached.outcome;
	}
	const raw = await send(() => cancellationApi.get(`${base(electionId)}/capabilities`, { validateStatus: all }));
	const outcome = toCapabilitiesOutcome(raw.status, raw.body);
	if (outcome.mode !== 'unknown') capabilitiesCache.set(electionId, { outcome, at: now() });
	return outcome;
};

/** Test seam: forget cached capabilities. */
export const resetCapabilitiesCache = () => capabilitiesCache.clear();

export const reportCancellation = async (electionId: number, body: IReportRequest): Promise<ApiResult<IReportResponse>> => {
	const raw = await send(() => cancellationApi.post(`${base(electionId)}/report`, body, { validateStatus: all }));
	return toReportResult(raw.status, raw.body);
};

/**
 * v1 fallback — ONLY when capabilities answered 404. A v2 server refuses Party Agents on the
 * bulk route, so calling this against v2 would turn every report into a 403.
 */
export const reportCancellationBaseline = async (
	electionId: number,
	body: IReportRequest
): Promise<ApiResult<IReportResponse>> => {
	const raw = await send(() =>
		cancellationApi.post(
			base(electionId),
			{ pollingUnitCodes: [body.pollingUnitCode], reason: body.reason, notes: body.notes, cancelledAt: body.cancelledAt },
			{ validateStatus: all }
		)
	);
	return toBaselineReportResult(raw.status, raw.body, body.pollingUnitCode);
};

export const withdrawCancellation = async (electionId: number, id: number): Promise<ApiResult<ICancellationRow>> => {
	const raw = await send(() => cancellationApi.post(`${base(electionId)}/${id}/withdraw`, undefined, { validateStatus: all }));
	return classifyResponse(raw.status, raw.body, pickRowEnvelope);
};

/**
 * The newest row for one unit in the caller's party ledger, any state. v2 filters by exact
 * code server-side; v1 has no code filter, so the first 100 rows are filtered here — and a miss
 * there means "not found in what we could see", which callers treat as unknown, not absent.
 */
export const findCancellationForUnit = async (
	electionId: number,
	pollingUnitCode: string,
	mode: 'v2' | 'baseline'
): Promise<ApiResult<ICancellationRow | null>> => {
	const params = mode === 'v2' ? { pollingUnitCode, state: 'All', pageSize: 20 } : { pageSize: 100 };
	const raw = await send(() => cancellationApi.get(base(electionId), { params, validateStatus: all }));
	return classifyResponse(raw.status, raw.body, (data) => {
		if (!Array.isArray(data)) return undefined;
		const rows = data.filter(isRow).filter((r) => r.pollingUnitCode === pollingUnitCode);
		rows.sort((a, b) => (b.recordedAt || '').localeCompare(a.recordedAt || ''));
		return rows[0] ?? null;
	});
};

/** Relay routes for one report's evidence: `sign`, `upload/{uploadId}/…`, and register (the base itself). */
export const evidenceBase = (electionId: number, id: number) => `${base(electionId)}/${id}/evidence`;

export interface ISignEvidenceRequest {
	kind: EvidenceKind;
	mime: string;
	bytes: number;
	/** The evidence item's local id (a UUID) — the server's idempotency key for the asset. */
	clientUploadId: string;
	fileName: string;
	sha256?: string;
}

/** Opens a MediaStash upload session through api.core. The bytes then go up via `media-relay.ts`. */
export const signEvidence = async (
	electionId: number,
	id: number,
	body: ISignEvidenceRequest
): Promise<ApiResult<IRelaySession>> => signRelay(evidenceBase(electionId, id), { ...body });

export const registerEvidence = async (
	electionId: number,
	id: number,
	body: IRegisterEvidenceRequest
): Promise<ApiResult<{ id: number }>> => {
	const raw = await send(() => cancellationApi.post(evidenceBase(electionId, id), body, { validateStatus: all }));
	return classifyResponse(raw.status, raw.body, (data) => {
		if (isObject(data) && isObject(data.evidence) && typeof data.evidence.id === 'number') return { id: data.evidence.id };
		return isObject(data) && typeof data.id === 'number' ? { id: data.id } : undefined;
	});
};

export const linkIncident = async (
	electionId: number,
	id: number,
	vesrIncidentId: string
): Promise<ApiResult<ICancellationRow>> => {
	const raw = await send(() =>
		cancellationApi.put(`${base(electionId)}/${id}/incident`, { vesrIncidentId }, { validateStatus: all })
	);
	return classifyResponse(raw.status, raw.body, pickRowEnvelope);
};
